import { Spin } from "antd";
import { CheckCircle, TrendingUp, Zap } from "lucide-react";
import type React from "react";
import type { UserInfo } from "@/types";

interface CreditInformationProps {
  userInfo: UserInfo | null;
  isLoading: boolean;
}

const CreditInformation: React.FC<CreditInformationProps> = ({ userInfo, isLoading }) => {
  if (isLoading) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <div className="flex justify-center items-center h-24">
          <Spin size="large" />
        </div>
      </div>
    );
  }

  if (!userInfo) {
    return (
      <div className="bg-white rounded-2xl border border-gray-200 p-6">
        <p className="text-red-500 text-sm">Failed to load credit information.</p>
      </div>
    );
  }

  const percentRemaining =
    userInfo.totalPurchasedCredits > 0
      ? (userInfo.availableCredits / userInfo.totalPurchasedCredits) * 100
      : 100;

  return (
    <div className="bg-gradient-to-br from-brand-600 to-brand-700 rounded-2xl p-6 text-white shadow-lg">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-sm font-medium text-brand-100">Credit Balance</h3>
        <div className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center">
          <Zap className="w-4 h-4" />
        </div>
      </div>

      {/* Main Balance */}
      <div className="mb-6">
        <div className="flex items-baseline gap-2">
          <span className="text-4xl font-bold">{userInfo.availableCredits.toLocaleString()}</span>
          <span className="text-brand-200 text-sm">credits</span>
        </div>

        {/* Progress Bar */}
        <div className="mt-4 h-2 bg-white/20 rounded-full overflow-hidden">
          <div
            className="h-full bg-white rounded-full transition-all duration-500"
            style={{ width: `${Math.min(percentRemaining, 100)}%` }}
          />
        </div>
        <p className="text-brand-200 text-xs mt-2">
          {percentRemaining.toFixed(0)}% of credits remaining
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4">
        <div className="bg-white/10 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <TrendingUp className="w-3.5 h-3.5 text-brand-200" />
            <span className="text-xs text-brand-200">Purchased</span>
          </div>
          <p className="text-lg font-semibold">{userInfo.totalPurchasedCredits.toLocaleString()}</p>
        </div>
        <div className="bg-white/10 rounded-xl p-3">
          <div className="flex items-center gap-2 mb-1">
            <CheckCircle className="w-3.5 h-3.5 text-brand-200" />
            <span className="text-xs text-brand-200">Used</span>
          </div>
          <p className="text-lg font-semibold">{userInfo.totalUsedCredits.toLocaleString()}</p>
        </div>
      </div>
    </div>
  );
};

export default CreditInformation;
